var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import '@ui5/webcomponents-icons/dist/hint.js';
import '@ui5/webcomponents-icons/dist/message-error.js';
import '@ui5/webcomponents-icons/dist/message-information.js';
import '@ui5/webcomponents-icons/dist/message-success.js';
import '@ui5/webcomponents-icons/dist/message-warning.js';
import '@ui5/webcomponents-icons/dist/question-mark.js';
import { enrichEventWithDetails, useI18nBundle, useIsomorphicLayoutEffect } from '@ui5/webcomponents-react-base';
import { ABORT, CANCEL, CLOSE, CONFIRMATION, DELETE, ERROR, HIGHLIGHT, IGNORE, INFORMATION, NO, OK, RETRY, SUCCESS, WARNING, YES } from '@ui5/webcomponents-react/dist/assets/i18n/i18n-defaults';
import clsx from 'clsx';
import React, { cloneElement, forwardRef, isValidElement, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { ButtonDesign } from '../../enums/ButtonDesign';
import { MessageBoxActions } from '../../enums/MessageBoxActions';
import { MessageBoxTypes } from '../../enums/MessageBoxTypes';
import { TitleLevel } from '../../enums/TitleLevel';
import { stopPropagation } from '../../internal/stopPropagation';
import { Button } from '../../webComponents/Button';
import { Dialog } from '../../webComponents/Dialog';
import { Icon } from '../../webComponents/Icon';
import { Title } from '../../webComponents/Title';
import { Text } from '../Text';
import styles from './MessageBox.jss';
const useStyles = createUseStyles(styles, { name: 'MessageBox' });
const createUniqueIds = (internalActions) => {
    return internalActions.map((action) => {
        if (typeof action === 'string') {
            return `${performance.now() + Math.random()}`.split('.')[1];
        }
        return null;
    });
};
const getIcon = (icon, type) => {
    if (isValidElement(icon))
        return icon;
    switch (type) {
        case MessageBoxTypes.Confirm:
            return React.createElement(Icon, { name: "question-mark" });
        case MessageBoxTypes.Error:
            return React.createElement(Icon, { name: "message-error" });
        case MessageBoxTypes.Information:
            return React.createElement(Icon, { name: "message-information" });
        case MessageBoxTypes.Success:
            return React.createElement(Icon, { name: "message-success" });
        case MessageBoxTypes.Warning:
            return React.createElement(Icon, { name: "message-warning" });
        case MessageBoxTypes.Highlight:
            return React.createElement(Icon, { name: "hint" });
        default:
            return null;
    }
};
const getActions = (actions, type) => {
    if (actions && actions.length > 0) {
        return actions;
    }
    if (type === MessageBoxTypes.Confirm) {
        return [MessageBoxActions.OK, MessageBoxActions.Cancel];
    }
    if (type === MessageBoxTypes.Error) {
        return [MessageBoxActions.Close];
    }
    return [MessageBoxActions.OK];
};
/**
 * The `MessageBox` component provides easier methods to create a `Dialog`, such as standard alerts, confirmation dialogs, or arbitrary message dialogs.
 * For convenience, it also provides an `open` prop, so it is not necessary to attach a `ref` to open the `MessageBox`.
 */
const MessageBox = forwardRef((props, ref) => {
    var _a;
    const { open, type, children, className, style, slot, titleText, icon, actions, emphasizedAction, onClose, initialFocus, accessibleName } = props, rest = __rest(props, ["open", "type", "children", "className", "style", "slot", "titleText", "icon", "actions", "emphasizedAction", "onClose", "initialFocus", "accessibleName"]);
    const classes = useStyles();
    const i18nBundle = useI18nBundle('@ui5/webcomponents-react');
    const actionTranslations = {
        [MessageBoxActions.Abort]: i18nBundle.getText(ABORT),
        [MessageBoxActions.Cancel]: i18nBundle.getText(CANCEL),
        [MessageBoxActions.Close]: i18nBundle.getText(CLOSE),
        [MessageBoxActions.Delete]: i18nBundle.getText(DELETE),
        [MessageBoxActions.Ignore]: i18nBundle.getText(IGNORE),
        [MessageBoxActions.No]: i18nBundle.getText(NO),
        [MessageBoxActions.OK]: i18nBundle.getText(OK),
        [MessageBoxActions.Retry]: i18nBundle.getText(RETRY),
        [MessageBoxActions.Yes]: i18nBundle.getText(YES)
    };
    const titleToRender = () => {
        if (titleText) {
            return titleText;
        }
        switch (type) {
            case MessageBoxTypes.Confirm:
                return i18nBundle.getText(CONFIRMATION);
            case MessageBoxTypes.Error:
                return i18nBundle.getText(ERROR);
            case MessageBoxTypes.Information:
                return i18nBundle.getText(INFORMATION);
            case MessageBoxTypes.Success:
                return i18nBundle.getText(SUCCESS);
            case MessageBoxTypes.Warning:
                return i18nBundle.getText(WARNING);
            case MessageBoxTypes.Highlight:
                return i18nBundle.getText(HIGHLIGHT);
            default:
                return null;
        }
    };
    const handleOnClose = (e) => {
        const { action } = e.target.dataset;
        stopPropagation(e);
        onClose(enrichEventWithDetails(e, { action }));
    };
    const messageBoxClassNames = clsx(classes.messageBox, className);
    const internalActions = getActions(actions, type);
    const [uniqueIds, setUniqueIds] = useState(() => createUniqueIds(internalActions));
    useIsomorphicLayoutEffect(() => {
        setUniqueIds(createUniqueIds(internalActions));
    }, [internalActions.length]);
    const getInitialFocus = () => {
        const indexOfInitialFocus = internalActions.indexOf(initialFocus);
        if (~indexOfInitialFocus && typeof internalActions[indexOfInitialFocus] === 'string') {
            return `${internalActions[indexOfInitialFocus]}-${uniqueIds[indexOfInitialFocus]}`;
        }
        return initialFocus;
    };
    const iconToRender = getIcon(icon, type);
    // @ts-ignore
    const { footer, headerText, onAfterClose } = rest, restWithoutOmitted = __rest(rest, ["footer", "headerText", "onAfterClose"]);
    return (React.createElement(Dialog, Object.assign({ open: open, slot: slot, ref: ref, style: style, className: messageBoxClassNames, onAfterClose: open ? handleOnClose : stopPropagation }, restWithoutOmitted, { accessibleName: accessibleName !== null && accessibleName !== void 0 ? accessibleName : `${(_a = titleToRender()) !== null && _a !== void 0 ? _a : ''} ${typeof children === 'string' ? children : ''}`, initialFocus: getInitialFocus(), "data-type": type }),
        !props.header && (React.createElement("header", { slot: "header", className: classes.header },
            iconToRender,
            iconToRender && React.createElement("span", { className: classes.spacer }),
            React.createElement(Title, { level: TitleLevel.H2 }, titleToRender()))),
        React.createElement(Text, null, children),
        React.createElement("footer", { slot: "footer", className: classes.footer }, internalActions.map((action, index) => {
            var _a, _b, _c, _d;
            if (typeof action !== 'string' && isValidElement(action)) {
                return cloneElement(action, {
                    onClick: ((_a = action === null || action === void 0 ? void 0 : action.props) === null || _a === void 0 ? void 0 : _a.onClick)
                        ? (e) => {
                            var _a;
                            (_a = action === null || action === void 0 ? void 0 : action.props) === null || _a === void 0 ? void 0 : _a.onClick(e);
                            handleOnClose(e);
                        }
                        : handleOnClose,
                    'data-action': (_c = (_b = action === null || action === void 0 ? void 0 : action.props) === null || _b === void 0 ? void 0 : _b['data-action']) !== null && _c !== void 0 ? _c : `${index}: custom action`
                });
            }
            return (React.createElement(Button, { id: `${action}-${uniqueIds[index]}`, key: `${action}-${index}`, design: emphasizedAction === action ? ButtonDesign.Emphasized : ButtonDesign.Transparent, onClick: handleOnClose, "data-action": action }, (_d = actionTranslations[action]) !== null && _d !== void 0 ? _d : action));
        }))));
});
MessageBox.displayName = 'MessageBox';
MessageBox.defaultProps = {
    open: false,
    type: MessageBoxTypes.Confirm,
    emphasizedAction: MessageBoxActions.OK,
    actions: []
};
export { MessageBox };
